// apps/admin/src/features/users/CreateAdminUserDialog.tsx
//
// Phase 8.2 Wave 7 Task 49 — Create Admin User dialog (System > Users).
//
// vg-binding: PLAN:task-49
// vg-binding: P8.D-98   — invite-email flow ONLY. No password input on the
//                          FE; the BE generates the invite token and sends
//                          the email when sendInvite=true.
// vg-binding: P8.D-99   — three submit modes: Create + Send Invite |
//                          Create (no invite) | Invite by Email (link only).
//                          Currently we ship the first two switched by the
//                          `send_invite` checkbox; the "invite-by-email"
//                          link-only mode is reachable from the row action
//                          on the parent table after the row exists.
// vg-binding: P8.D-380  — success toast copy:
//                          "Đã tạo tài khoản và gửi email mời (N gửi thành công)"
// vg-binding: P8.D-474  — Idempotency-Key generated client-side (uuidv4) so
//                          accidental double-submit collapses to one account
//                          + one email dispatch.
// vg-binding: P8.D-486  — error envelope unwrap: user_message > message >
//                          "Network error" fallback.
//
// Test IDs (per task-49 spec block):
//   admin-users-create-dialog              — root
//   admin-users-create-email-input         — email field
//   admin-users-create-role-select         — role picker
//   admin-users-create-autogen-pwd-checkbox — auto-gen password / send invite
//   admin-users-create-submit-invite-btn   — primary submit (send invite)
//   admin-users-create-submit-no-invite-btn — secondary submit (no email)
//
// The third "Invite by email" path (link-only, no row creation) is NOT
// exposed from this dialog — the row action button on the parent table
// drives that flow once the user record exists.

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  getCreateAdminUserRequestSchema,
  createAdminUser,
  newIdempotencyKey,
  type AdminUserApiError,
  type CreateAdminUserRequest,
  type CreateAdminUserResponse,
} from '../../api/admin-users.api.js';
import { adminUserKeys } from '../../api/queries/adminUserKeys.js';
import { ADMIN_GLOBAL_ROLES } from '../../lib/permissions.js';

// ---------------------------------------------------------------------------
// Role picker options
//
// We expose the 6 grantable roles from the Phase 1 GRANT_REVOKE_ROLE_TYPES
// enum, intentionally excluding `superadmin` (creation must go through a
// dedicated SuperAdmin bootstrap; forbids it from the create dialog
// to avoid privilege-escalation footguns). `accountant` is included per
// kế toán is a first-class create-flow role.
// ---------------------------------------------------------------------------

// Role enum values; visible labels resolved via i18n
// (`admin_user.createDialog.roleOptions.<value>`) at render time.
// `accountant` is included (kế toán = first-class role).
const ROLE_VALUES: ReadonlyArray<CreateAdminUserRequest['roleType']> = [
  'admin',
  'support',
  'saler',
  'manager',
  'accountant',
  'affiliate',
] as const;

// Sanity assertion — keep ROLE_OPTIONS in lock-step with ADMIN_GLOBAL_ROLES.
// (Touch-friendly: when a new tier-1 role is added to permissions.ts, this
// surface either compiles with the new option appended or fails review.)
void ADMIN_GLOBAL_ROLES.ACCOUNTANT;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreateAdminUserDialogProps {
  /** Controlled open state — parent owns the toggle. */
  open: boolean;
  onClose: () => void;
  /** Optional callback fired on a successful create (parent may refetch). */
  onCreated?: (payload: CreateAdminUserResponse) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateAdminUserDialog({
  open,
  onClose,
  onCreated,
}: CreateAdminUserDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('users');
  const { t: tAdmin } = useTranslation('admin');
  const queryClient = useQueryClient();

  // Re-mount the Idempotency-Key per dialog OPEN cycle so each submitted
  // form carries a unique key. the same key is reused across
  // any retry within the SAME dialog session (Cancel + reopen rotates it).
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    newIdempotencyKey(),
  );

  // Track which submit path the user picked so the success toast can read
  // the chosen mode without inspecting form state after reset.
  const [submitMode, setSubmitMode] = useState<'invite' | 'no_invite' | null>(
    null,
  );

  const createSchema = useMemo(() => getCreateAdminUserRequestSchema(tAdmin), [tAdmin]);

  const form = useForm<CreateAdminUserRequest>({
    resolver: zodResolver(createSchema as never),
    defaultValues: {
      email: '',
      displayName: '',
      roleType: 'admin',
      sendInvite: true,
    },
    mode: 'onChange',
  });

  // Reset form + rotate Idempotency-Key on each open.
  useEffect(() => {
    if (open) {
      form.reset({
        email: '',
        displayName: '',
        roleType: 'admin',
        sendInvite: true,
      });
      setIdempotencyKey(newIdempotencyKey());
      setSubmitMode(null);
    }
    // form.reset is stable; lint exhaustive-deps would force a re-run loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation<
    CreateAdminUserResponse,
    AdminUserApiError,
    CreateAdminUserRequest
  >({
    mutationFn: (body) => createAdminUser(body, idempotencyKey),
    onSuccess: (payload) => {
      // Invalidate every list slice so the new row surfaces without a
      // manual refresh. Detail slices are unaffected (this is a new id).
      void queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });

      const inviteSent = payload.invite_sent;
      //  copy — branch on whether the invite email was dispatched.
      // "(N gửi thành công)" wording matches the spec exactly for the
      // single-send case (N=1); when sendInvite=false we drop the suffix.
      const title = inviteSent
        ? t('admin_user.createDialog.toast.successInvite')
        : t('admin_user.createDialog.toast.successNoInvite');
      toast({
        title,
        description: `${payload.user.email} — ${payload.user.primaryRole}`,
        variant: 'success',
      });
      onCreated?.(payload);
      onClose();
    },
    onError: (err) => {
      //  priority: err already comes pre-unwrapped from
      // admin-users.api.ts with `userMessage` set to user_message |
      // message | "Network error" fallback.
      toast({
        title: t('admin_user.createDialog.toast.failTitle'),
        description: err?.userMessage ?? t('admin_user.createDialog.toast.failDesc'),
        variant: 'error',
      });

      // Surface field-level errors on the matching RHF inputs so the user
      // can correct without guessing which field the BE rejected.
      if (err?.fieldErrors) {
        for (const [field, msgs] of Object.entries(err.fieldErrors)) {
          const message = Array.isArray(msgs) ? msgs[0] : String(msgs);
          if (!message) continue;
          // Field names BE sends are snake_case; the RHF state keys are
          // camelCase. Map the two well-known fields explicitly.
          const rhfField = (
            {
              email: 'email',
              display_name: 'displayName',
              displayName: 'displayName',
              role_type: 'roleType',
              roleType: 'roleType',
            } as Record<string, keyof CreateAdminUserRequest>
          )[field];
          if (rhfField) {
            form.setError(rhfField, { type: 'server', message });
          }
        }
      }
    },
  });

  const handleSubmitInvite = form.handleSubmit((values) => {
    setSubmitMode('invite');
    mutation.mutate({ ...values, sendInvite: true });
  });

  const handleSubmitNoInvite = form.handleSubmit((values) => {
    setSubmitMode('no_invite');
    mutation.mutate({ ...values, sendInvite: false });
  });

  // Disable the form while the mutation is in flight so a user cannot
  // double-submit OR flip the Idempotency-Key mid-send by toggling modes.
  const submitting = mutation.isPending;

  const sendInviteValue = form.watch('sendInvite');

  // Sync the visible "send invite" checkbox state with whichever button
  // the user is leaning toward (purely cosmetic — actual submit path is
  // driven by the button handlers above so the field value is overridden
  // on dispatch).
  const checkboxLabel = useMemo(() => {
    if (sendInviteValue) {
      return t('admin_user.createDialog.checkbox.onLabel');
    }
    return t('admin_user.createDialog.checkbox.offLabel');
  }, [sendInviteValue, t]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !submitting) onClose();
      }}
      className="max-w-lg"
      data-testid="admin-users-create-dialog"
    >
      <DialogHeader>
        <DialogTitle>{t('admin_user.createDialog.title')}</DialogTitle>
      </DialogHeader>

      <Form
        onSubmit={handleSubmitInvite}
        aria-labelledby="admin-users-create-title"
        className="mt-4 flex flex-col gap-4 px-6 pb-2"
      >
        {/* Email --------------------------------------------------------- */}
        <FormField>
          <FormLabel htmlFor="admin-users-create-email-input">
            {t('admin_user.createDialog.fields.email')} <span className="text-red-600 dark:text-red-300">*</span>
          </FormLabel>
          <FormControl>
            <Input
              id="admin-users-create-email-input"
              data-testid="admin-users-create-email-input"
              type="email"
              autoComplete="email"
              placeholder="user@example.com"
              {...form.register('email')}
              aria-invalid={Boolean(form.formState.errors.email)}
              disabled={submitting}
            />
          </FormControl>
          {form.formState.errors.email && (
            <FormMessage>{form.formState.errors.email.message}</FormMessage>
          )}
        </FormField>

        {/* Display name -------------------------------------------------- */}
        <FormField>
          <FormLabel htmlFor="admin-users-create-name-input">
            {t('admin_user.createDialog.fields.displayName')} <span className="text-red-600 dark:text-red-300">*</span>
          </FormLabel>
          <FormControl>
            <Input
              id="admin-users-create-name-input"
              data-testid="admin-users-create-name-input"
              type="text"
              autoComplete="name"
              {...form.register('displayName')}
              aria-invalid={Boolean(form.formState.errors.displayName)}
              disabled={submitting}
            />
          </FormControl>
          {form.formState.errors.displayName && (
            <FormMessage>
              {form.formState.errors.displayName.message}
            </FormMessage>
          )}
        </FormField>

        {/* Role select --------------------------------------------------- */}
        <FormField>
          <FormLabel htmlFor="admin-users-create-role-select">
            {t('admin_user.createDialog.fields.role')} <span className="text-red-600 dark:text-red-300">*</span>
          </FormLabel>
          <FormControl>
            <Select
              value={form.watch('roleType')}
              onValueChange={(value) =>
                form.setValue(
                  'roleType',
                  value as CreateAdminUserRequest['roleType'],
                  { shouldValidate: true, shouldDirty: true },
                )
              }
              disabled={submitting}
            >
              <SelectTrigger
                id="admin-users-create-role-select"
                data-testid="admin-users-create-role-select"
                aria-invalid={Boolean(form.formState.errors.roleType)}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_VALUES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`admin_user.createDialog.roleOptions.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
          {form.formState.errors.roleType && (
            <FormMessage>{form.formState.errors.roleType.message}</FormMessage>
          )}
        </FormField>

        {/* Send invite checkbox — DEFAULTS ON ---------------- */}
        <FormField>
          <label className="flex items-start gap-2 text-sm text-pw-text">
            <input
              type="checkbox"
              data-testid="admin-users-create-autogen-pwd-checkbox"
              className="mt-0.5 h-4 w-4 rounded border border-pw-border accent-pw-primary"
              checked={Boolean(sendInviteValue)}
              onChange={(e) =>
                form.setValue('sendInvite', e.target.checked, {
                  shouldDirty: true,
                })
              }
              disabled={submitting}
            />
            <span>
              <span className="font-medium">{t('admin_user.createDialog.checkbox.title')}</span>
              <span className="block text-pw-text-muted">{checkboxLabel}</span>
              <span className="mt-1 block text-xs text-pw-text-muted">
                {t('admin_user.createDialog.checkbox.help')}
              </span>
            </span>
          </label>
        </FormField>

        {/* Idempotency-Key surfaced for transparency in dev — hidden in
            production builds. Useful when debugging duplicate-submit
            collapse from the network panel.                            */}
        {import.meta.env.DEV && (
          <div className="text-xs text-pw-text-muted">
            <span className="font-mono">Idempotency-Key: {idempotencyKey}</span>
          </div>
        )}
      </Form>

      <DialogFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={submitting}
          data-testid="admin-users-create-cancel-btn"
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="ghost"
          data-testid="admin-users-create-submit-no-invite-btn"
          disabled={submitting}
          onClick={() => {
            form.setValue('sendInvite', false, { shouldDirty: true });
            void handleSubmitNoInvite();
          }}
        >
          {submitting && submitMode === 'no_invite'
            ? t('admin_user.createDialog.submitNoInviteLoading')
            : t('admin_user.createDialog.submitNoInvite')}
        </Button>
        <Button
          variant="default"
          data-testid="admin-users-create-submit-invite-btn"
          disabled={submitting}
          onClick={() => {
            form.setValue('sendInvite', true, { shouldDirty: true });
            void handleSubmitInvite();
          }}
        >
          {submitting && submitMode === 'invite'
            ? t('admin_user.createDialog.submitInviteLoading')
            : t('admin_user.createDialog.submitInvite')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
